/* Native progressive enhancement: original content and commerce remain usable without JS. */
class ProteinHero extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = 0;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.reduced.matches;
    this.hovered = false;
    this.focused = false;
    this.toggle = this.querySelector('[data-pause]');
    this.querySelectorAll('[data-to]').forEach(button => button.addEventListener('click', () => {
      this.show(Number(button.dataset.to)); this.restart();
    }));
    this.toggle?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.updateToggle(); this.restart();
    });
    this.onVisibility = () => this.restart();
    this.onMotion = () => { this.paused = this.reduced.matches; this.updateToggle(); this.restart(); };
    document.addEventListener('visibilitychange', this.onVisibility);
    this.reduced.addEventListener('change', this.onMotion);
    this.addEventListener('mouseenter', () => { this.hovered = true; this.restart(); });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.restart(); });
    this.addEventListener('focusin', () => { this.focused = true; this.restart(); });
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) { this.focused = false; this.restart(); }
    });
    this.updateToggle(); this.show(0);
    const firstImage = this.slides[0]?.querySelector('img');
    const begin = () => { this.loaded = true; this.restart(8000); };
    if (!firstImage || firstImage.complete) begin();
    else { firstImage.addEventListener('load', begin, { once: true }); firstImage.addEventListener('error', begin, { once: true }); }
  }
  hydrate(index) {
    const slide = this.slides[index];
    const template = slide?.querySelector('template');
    if (template) {
      const fragment = template.content.cloneNode(true);
      const image = fragment.querySelector('img');
      if (image) { image.loading = 'eager'; image.fetchPriority = 'low'; }
      template.replaceWith(fragment);
    }
  }
  show(index) {
    if (!this.slides[index]) return;
    this.hydrate(index);
    this.index = index;
    this.slides.forEach((slide, number) => {
      slide.classList.toggle('is-active', number === index);
      slide.setAttribute('aria-hidden', String(number !== index));
      slide.tabIndex = number === index ? 0 : -1;
    });
    this.querySelectorAll('[data-to]').forEach((button, number) => button.setAttribute('aria-pressed', String(number === index)));
  }
  updateToggle() {
    if (!this.toggle) return;
    this.toggle.textContent = this.paused ? '▶' : 'Ⅱ';
    this.toggle.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
  }
  restart(delay = 4000) {
    clearTimeout(this.timer); clearTimeout(this.preloadTimer);
    if (!this.loaded || this.paused || this.hovered || this.focused || document.hidden || this.slides.length < 2) return;
    const next = (this.index + 1) % this.slides.length;
    this.preloadTimer = setTimeout(() => this.hydrate(next), Math.max(1200, delay - 1800));
    this.timer = setTimeout(() => { this.show(next); this.restart(); }, delay);
  }
  disconnectedCallback() {
    clearTimeout(this.timer); clearTimeout(this.preloadTimer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.reduced?.removeEventListener('change', this.onMotion);
  }
}
if (!customElements.get('protein-hero')) customElements.define('protein-hero', ProteinHero);

/* A small opacity-only logo layer starts after the critical page load. */
const enableMotion = () => {
  document.documentElement.classList.add('ps-motion-ready');
  const update = () => document.documentElement.classList.toggle('ps-motion-paused', document.hidden);
  document.addEventListener('visibilitychange', update); update();
  if ('IntersectionObserver' in window) {
    const reveal = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('ps-onstage'); reveal.unobserve(entry.target); }
    }), { threshold: .12 });
    document.querySelectorAll('.ps-guide,.ps-store,.ps-instagram').forEach(el => reveal.observe(el));
  }
};
if (document.readyState === 'complete') enableMotion();
else window.addEventListener('load', enableMotion, { once: true });

